import React from 'react';
import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material';
import { CheckCircle, ErrorOutline, RadioButtonUnchecked } from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import StatusChip from '../../../shared/components/StatusChip';
import FormActionBar from '../../../shared/components/FormActionBar';
import type { AwsAccount, Customer } from '../../../types/backend';
import { IDENTITY_FIELDS, type useAccountForm } from '../hooks/useAccountForm';
import { useAccountSectionSave } from '../hooks/useAccountSectionSave';
import { useAccountBaselineStatus } from '../hooks/useAccountBaselineStatus';
import SectionSaveOutcome from './SectionSaveOutcome';
import { summarizeBaselines } from '../baselineStatus';
import {
  isOnboarded,
  needsAttention,
  onboardingChecklist,
  regionCity,
  type ChecklistStep,
} from '../accountTabs';

const STEP_ICON: Record<ChecklistStep['state'], React.ReactElement> = {
  done: <CheckCircle fontSize="small" color="success" />,
  current: <RadioButtonUnchecked fontSize="small" color="primary" />,
  problem: <ErrorOutline fontSize="small" color="error" />,
  todo: <RadioButtonUnchecked fontSize="small" color="disabled" />,
};

const Checklist: React.FC<{ steps: ChecklistStep[] }> = ({ steps }) => (
  <Box component="ol" aria-label="Onboarding checklist" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {steps.map((step) => (
      <Box component="li" key={step.title} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ pt: 0.25 }}>{STEP_ICON[step.state]}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }} color={step.state === 'todo' ? 'text.secondary' : 'text.primary'}>
              {step.title}
            </Typography>
            <Typography variant="caption" color={step.state === 'problem' ? 'error.main' : 'text.secondary'}>
              {step.status}
            </Typography>
          </Box>
          {step.body && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0, mt: 0.5 }}>
              {step.body}
            </Typography>
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/** The checklist while onboarding, the state line and what needs attention after. */
const OnboardingState: React.FC<{ customerId: string; account: AwsAccount }> = ({ customerId, account }) => {
  const { accounts, error } = useAccountBaselineStatus(customerId);
  const view = accounts.find((a) => a.aws_account_id === account.aws_account_id);
  if (error) return <InlineAlert severity="error">Could not read the baseline status</InlineAlert>;
  if (!view) return null;

  const baselines = summarizeBaselines(view);
  if (!isOnboarded(baselines)) {
    return (
      <Checklist
        steps={onboardingChecklist(view, {
          accountName: account.account_name,
          mainRegion: account.main_region,
          otherRegions: (account.supported_regions ?? []).filter((r) => r !== account.main_region).length,
        })}
      />
    );
  }

  const attention = needsAttention(baselines);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <InlineAlert severity={attention.length ? 'warning' : 'success'}>
        {attention.length ? `Onboarded · ${attention.length} needs attention` : 'Onboarded · all baselines in sync'}
      </InlineAlert>
      {attention.length > 0 && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.75 }}>Needs attention</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {attention.map((b) => (
              <Box key={b.name} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">{b.title}</Typography>
                  <StatusChip status={b.health} />
                </Box>
                {b.reason && <Typography variant="caption" color="text.secondary">{b.reason}</Typography>}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const yesNo = (value: boolean | null | undefined, yes: string, no: string) =>
  value === null || value === undefined ? 'Unknown' : value ? yes : no;

/** The account's name and Active, the only edits Overview holds, with their own save. */
const Identity: React.FC<{ form: ReturnType<typeof useAccountForm>; onDelete?: () => void }> = ({ form, onDelete }) => {
  const section = useAccountSectionSave(form, IDENTITY_FIELDS);
  return (
    <Box component="section" aria-label="Account identity" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Account Name"
        value={form.formData.account_name}
        onChange={(e) => form.handleChange('account_name', e.target.value)}
        error={section.fieldError('account_name') !== null}
        helperText={section.fieldError('account_name') || 'Friendly name for this account'}
        InputLabelProps={{ shrink: true }}
        sx={{ maxWidth: 480 }}
      />
      <FormControlLabel
        control={(
          <Switch
            checked={form.formData.enabled}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => form.handleChange('enabled', e.target.checked)}
          />
        )}
        label={(
          <Box>
            <Typography variant="body2">Active</Typography>
            <Typography variant="caption" color="text.secondary">
              A disabled account is skipped, including by onboarding
            </Typography>
          </Box>
        )}
      />
      <SectionSaveOutcome section={section} />
      <FormActionBar
        onSave={section.save}
        saving={section.saving}
        saveDisabled={section.saving || !section.dirty}
        onDelete={onDelete}
        deleteLabel="Delete Account"
        sx={{ mt: 0 }}
      />
    </Box>
  );
};

export interface AccountOverviewTabProps {
  customer: Customer;
  account: AwsAccount;
  baselineEnabled: boolean;
  form: ReturnType<typeof useAccountForm>;
  /** customers:UpdateAWSAccount's level (L2 and up): the name and Active. */
  canEdit: boolean;
  /** Admin only: deleting the account. */
  onDelete?: () => void;
}

const AccountOverviewTab: React.FC<AccountOverviewTabProps> = ({ customer, account, baselineEnabled, form, canEdit, onDelete }) => {
  // The two decisions come from the baseline status, the same read the
  // Baselines switches use, so a switch moved there shows here at once.
  const status = useAccountBaselineStatus(customer.id, baselineEnabled);
  const view = status.accounts.find((a) => a.aws_account_id === account.aws_account_id);
  const decision = (value: boolean | undefined, yes: string, no: string) =>
    status.error ? 'Could not read' : view ? yesNo(value, yes, no) : status.loading ? 'Reading' : 'Unknown';

  // No account name: the list beside this carries it, and so does the name field.
  const facts: Array<[string, string]> = [
    ['AWS account ID', account.aws_account_id],
    ['Customer', customer.name],
    ['Operations account', customer.operations_account_id || 'Not configured'],
    ['Main region', `${regionCity(account.main_region)} (${account.main_region})`],
    ['Supported regions', (account.supported_regions ?? []).map(regionCity).join(', ')],
    // Where Active is editable its switch says it.
    ...(canEdit ? [] : [['Account active', account.enabled ? 'Yes' : 'No'] as [string, string]]),
    ...(baselineEnabled ? [
      ['Cloud2 keeps the baselines', decision(view?.baseline_managed, 'Yes', 'Not yet')] as [string, string],
      ['Customer controls admin access', decision(view?.break_glass_required, 'On', 'Off')] as [string, string],
    ] : []),
    ['External ID', account.external_id || 'Not set'],
  ];

  return (
    <Box>
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, maxWidth: 820, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {baselineEnabled && <OnboardingState customerId={customer.id} account={account} />}
        <Box component="section" aria-label="Account facts">
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>Account</Typography>
          <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            {facts.map(([k, v]) => (
              <Box key={k} sx={{ minWidth: 0 }}>
                <Typography component="dt" variant="caption" color="text.secondary">{k}</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{v}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        {canEdit && <Identity form={form} onDelete={onDelete} />}
      </Box>
    </Box>
  );
};

export default AccountOverviewTab;
